import {
  useResponsiveFontSize,
  useResponsiveHeight,
  useResponsiveWidth,
} from '@/components/Util/reactResponsiveDimensions';
import SquareGrid from '@/components/Util/reactSquareGrid';
import { useStoreActions, useStoreState } from '@/stores/store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Image,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { Button } from '@/components/ui/therapist/Button';
import { Text } from '@/components/Text';
import { Switch } from '@/components/Switch';
import { downloadPracticeSessions } from '../api/services/practicesessions';
import { downloadResults, newDownloadResults } from '../api/services/results';
import { toggleTherapistCanSeeResults } from '../api/services/user';
import ConfirmModal from '@/components/SharedComponents/ConfirmModal/ConfirmModal';
import OfflineImage from '@/components/SharedComponents/OfflineImage/OfflineImage';
import colors, { appTheme } from '@/config/colors';
import { useCommonStyles } from '@/hooks/useCommonStyles';
import encryptedStorage from '../stores/encryptedStorage';
import store from '../stores/store';
import layout from '../styles/layout';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { Toggle } from '@/components/ui/therapist/toggle';

const styles = StyleSheet.create({
  setImageWrapper: {
    position: 'relative',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  exerciseSetImageStyle: {
    flex: 1,
    width: '100%',
    resizeMode: 'contain',
    borderRadius: 1,
  },
  exerciseSetImageCard: {
    width: '55%',
    height: '55%',
    borderColor: '#6d6c7c',
    borderStyle: 'solid',
    position: 'absolute',
    zIndex: 1,
    backgroundColor: 'white',
  },
  imageStackBackground: {
    zIndex: 0,
    borderColor: '#6d6c7c',
    borderStyle: 'solid',
    width: '55%',
    height: '55%',
    marginLeft: '5%',
    marginTop: '5%',
    backgroundColor: '#e8e7e7',
    transform:
      Platform.OS === 'ios' ? [{ rotate: '0deg' }] : [{ rotate: '2deg' }],
  },
  imageBorderRadius: {
    borderRadius: 1,
  },
  allowTherapistSwitchContainer: {
    alignItems: 'center',
  },
  allowTherapistSwitchWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '75%',
    backgroundColor: colors.white,
  },

  linkStyle: { color: colors.darkPurpleBlue },

  centerPosition: {
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  header: {
    padding: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  full1: {
    flex: 1,
  },
  full2: {
    flex: 2,
  },
  textHeader: {
    color: appTheme.textColor,
  },
  centerCardContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 20,
  },
  paddingForMobileCardContainer: {
    padding: 7,
  },
});

function ToggleTherapistCanSeeResults({ showErrorMessage }) {
  const therapistCanSeeResults = useStoreState(
    (state) => state.signedInUser.user.therapistCanSeeResults,
  );
  const localToggle = useStoreActions(
    (actions) => actions.toggleTherapistCanSeeResults,
  );
  const [checked, setChecked] = useState(therapistCanSeeResults ?? false);

  const switchToggleValue = async () => {
    const result = await toggleTherapistCanSeeResults({
      therapistCanSeeResults: !checked,
    });
    if (result) {
      localToggle(!checked);
      setChecked(!checked);
    } else {
      showErrorMessage();
    }
  };
  return <Toggle value={checked} onToggle={switchToggleValue} />;
}

const startsWithNumber = (str) => {
  const regex = /^[0-9]/; // Regex to match the first character as a number (0-9)

  return regex.test(str);
};

function Results(props) {
  const [sets, setSets] = useState([]);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showResultsDownloadModal, setShowResultsDownloadModal] =
    useState(false);
  const [
    showPracticeSessionDownloadModal,
    setShowPracticeSessionDownloadModal,
  ] = useState(false);
  const [mobileDownloadRedirectModal, setMobileDownloadRedirectModal] =
    useState(false);
  const [errorOnToggle, setErrorOnToggle] = useState(false);

  const { navigation } = props;
  const { accessToken } = useStoreState(
    (state) => state.signedInUser,
    () => true,
  );

  const commonStyles = useCommonStyles();
  const responsiveFontSize = useResponsiveFontSize(1);
  let isLandscape = useResponsiveHeight(100) < useResponsiveWidth(100);
  const getResponsiveFontSize = (factor) => {
    return responsiveFontSize * factor;
  };

  function useResponsiveSize(number) {
    if (isLandscape) {
      return useResponsiveWidth(number);
    } else {
      return useResponsiveHeight(number);
    }
  }

  const showErrorMessage = useCallback(() => {
    setErrorOnToggle(true);
  }, []);

  const removeErrorMessage = useCallback(() => {
    setErrorOnToggle(false);
  }, []);

  let responsiveStyles = StyleSheet.create({
    container: {
      paddingVertical: useResponsiveFontSize(1),
      // height: Dimensions.get('window').height,r
      flexDirection: 'column',
    },

    exerciseSetImageCardBorder: {
      borderWidth: useResponsiveSize(0.3),
      // padding: useResponsiveSize(0.2),
      borderRadius: useResponsiveSize(0.2),
    },

    exercisesetTitleText: {
      textAlign: 'center',
      fontSize: useResponsiveSize(1.5),
    },
    setBoxesWrapper: {
      width: getResponsiveFontSize(34),
      height: getResponsiveFontSize(34),
    },
    switchLabelStyle: {
      fontWeight: '700',
      fontSize: getResponsiveFontSize(1.5),
      marginVertical: getResponsiveFontSize(0.6),
    },
    footerTextStyle: {
      textAlign: 'center',
      fontSize: getResponsiveFontSize(1.3),
    },
    responsiveSwitcherWrapperStyle: {
      marginVertical: useResponsiveFontSize(1),
    },
    footerContainer: {
      paddingBottom: getResponsiveFontSize(2),
      alignItems: 'center',
      justifyContent: 'center',
    },
    wrapperPadding: {
      padding: useResponsiveWidth(1),
    },
  });

  const determineSetType = (setType) => {
    if (setType === 'textset') {
      return 'Texte';
    } else if (setType === 'wordset') {
      return 'Wörter';
    } else if (setType === 'sentenceset') {
      return 'Sätze';
    }
  };

  const navigateToResult = useCallback((options) => {
    navigation.navigate('ResultDetailScreen', options);
  }, []);

  // add exercise sets to be rendered as SquareGrid
  const exerciseSets = useMemo(() => {
    const user = store.getState().signedInUser?.user;
    return (sets ?? [])
      .map((set, key) => {
        if (!set?.exerciseClass || !set?.activeWordset) return null;
        const type = `${set.exerciseClass}set`;
        const activeWordset = set.activeWordset?.[type];
        const items = activeWordset?.[type + 'Items'] ?? [];
        const appDisplayName = activeWordset?.appDisplayName;

        const onPress = () => {
          const fullName = user
            ? `${user.firstName ?? ''} ${user.lastName ?? ''}`
            : 'Gast';
          const options = {
            patientId: set.patientId,
            exerciseClass: type,
            setId: activeWordset._id,
            patientName: fullName,
            exerciseSet: determineSetType(type),
            exerciseSetName: appDisplayName,
          };
          navigateToResult(options);
        };

        if (!items.length) return null;
        const first = items[0] ?? {};
        const imageUri =
          first.word?.pictures?.[0] ?? first.sentence?.pictures?.[0] ?? null;

        if (type !== 'textset') {
          return (
            <PressableNeolexon
              testID={determineSetType(type)?.toLowerCase()}
              key={key}
              style={[
                layout.width90Height90,
                layout.exercisesetCard,
                Platform.OS === 'web'
                  ? styles.centerCardContainer
                  : styles.paddingForMobileCardContainer,
              ]}
              onPress={onPress}
            >
              <View>
                <Text
                  style={[
                    responsiveStyles.exercisesetTitleText,
                    layout.darkGrey,
                  ]}
                  numberOfLines={1}
                >
                  {appDisplayName ? appDisplayName : determineSetType(type)}
                </Text>
              </View>
              <View style={[styles.setImageWrapper]}>
                <View style={[styles.setImageWrapper]}>
                  <View
                    style={[
                      styles.imageStackBackground,
                      responsiveStyles.exerciseSetImageCardBorder,
                    ]}
                  />
                </View>
                <View
                  style={[
                    styles.exerciseSetImageCard,
                    responsiveStyles.exerciseSetImageCardBorder,
                  ]}
                >
                  {type === 'textset' ? (
                    <Image
                      source={require('../assets/text_icon.png')}
                      style={[
                        styles.exerciseSetImageStyle,
                        styles.imageBorderRadius,
                      ]}
                    />
                  ) : (
                    <OfflineImage
                      source={{ uri: imageUri }}
                      style={[
                        styles.exerciseSetImageStyle,
                        styles.imageBorderRadius,
                      ]}
                    />
                  )}
                </View>
              </View>
            </PressableNeolexon>
          );
        }
      })
      .filter(Boolean);
  }, [sets]);

  const handleWebClickDownload = (type) => {
    return () => {
      // type param is saved as a closure for using the same function
      if (type === 'results') {
        downloadResults(accessToken);
        setShowResultsDownloadModal(false);
      }
      if (type === 'sessions') {
        downloadPracticeSessions(accessToken);
        setShowPracticeSessionDownloadModal(false);
      }
    };
  };

  const handleBrowserRedirectClick = () => {
    // openURL("https://aphasie.neolexon.de");
    setMobileDownloadRedirectModal(false);
  };

  const onDownloadModalShow = useCallback(() => {
    if (Platform.OS === 'web') {
      setShowDownloadModal(true);
    } else {
      setMobileDownloadRedirectModal(true);
    }
  }, []);

  const onDownloadCancel = useCallback(() => {
    setShowDownloadModal(false);
  }, []);

  const onDownloadPress = useCallback(async () => {
    newDownloadResults(accessToken);
    setShowDownloadModal(false);
  }, []);

  useEffect(() => {
    const generateSets = async () => {
      const storageKeys = await encryptedStorage().listItems();
      const setsPromises = [];
      for (let key of storageKeys) {
        if (!startsWithNumber(key)) continue;
        setsPromises.push(encryptedStorage().getItem(key));
      }
      const sets = await Promise.all(setsPromises);
      setSets(sets);
    };
    generateSets();
  }, []);

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <Button
          label="Zurück"
          icon="chevronLeft"
          onPress={() => navigation.goBack()}
        />
      ),
    });
  }, []);

  return (
    <ScrollView style={styles.full1}>
      <View style={responsiveStyles.container}>
        <ConfirmModal
          isVisible={showResultsDownloadModal}
          cancelText={'Abbrechen'}
          confirmText={'Herunterladen'}
          confirmAction={handleWebClickDownload('results')}
          cancelAction={() => setShowResultsDownloadModal(false)}
          headerText={'Ergebnisse herunterladen'}
          contentText={
            'Wollen Sie Ihre Übungsergebnisse wirklich herunterladen? Dies sollte nur auf eigenen Geräten durchgeführt werden, um zu verhindern, dass Unbefugte Zugriff auf die heruntergeladenen Daten erhalten.'
          }
        />
        <ConfirmModal
          isVisible={mobileDownloadRedirectModal}
          confirmText={'Schließen'}
          confirmAction={handleBrowserRedirectClick}
          headerText={'Nur in der Browser-App möglich'}
          isContentComponent
          contentText={
            <View>
              <Text
                style={{
                  fontSize: useResponsiveFontSize(3),
                  padding: useResponsiveWidth(2.8),
                }}
              >
                Das Herunterladen der App-Daten ist in der Browser-App am
                Computer auf{' '}
                <Text
                  style={styles.linkStyle}
                  onPress={() => Linking.openURL('https://aphasie.neolexon.de')}
                >
                  https://aphasie.neolexon.de
                </Text>{' '}
                unter Ergebnisse ansehen möglich.
              </Text>
            </View>
          }
        />
        <ConfirmModal
          isVisible={errorOnToggle}
          confirmText={'Schließen'}
          confirmAction={removeErrorMessage}
          headerText={'Fehler beim Speichern.'}
          isContentComponent
          contentText={
            <View>
              <Text
                style={{
                  fontSize: useResponsiveFontSize(3),
                  padding: useResponsiveWidth(2.8),
                }}
              >
                Zum Speichern der Einstellung ist Internet erforderlich. Bitte
                überprüfen Sie Ihre Internetverbindung und versuchen es erneut.
              </Text>
            </View>
          }
        />
        <ConfirmModal
          isVisible={showPracticeSessionDownloadModal}
          cancelText={'Abbrechen'}
          confirmText={'Herunterladen'}
          confirmAction={handleWebClickDownload('sessions')}
          cancelAction={() => setShowPracticeSessionDownloadModal(false)}
          headerText={'Daten herunterladen'}
          contentText={
            'Wollen Sie Ihre Daten zur Übungsdauer wirklich herunterladen? Dies sollte nur auf eigenen Geräten durchgeführt werden, um zu verhindern dass Unbefugte Zugriff auf die heruntergeladenen Daten erhalten.'
          }
        />
        <ConfirmModal
          isVisible={showDownloadModal}
          cancelText={'Abbrechen'}
          confirmText={'Herunterladen'}
          confirmAction={onDownloadPress}
          cancelAction={onDownloadCancel}
          headerText={'App-Daten herunterladen'}
          contentText={
            'Wollen Sie Ihre App-Daten wirklich herunterladen? Dies sollte nur auf eigenen Geräten durchgeführt werden, um zu verhindern, dass Unbefugte Zugriff auf die heruntergeladenen Daten erhalten.'
          }
        />
        <View style={styles.header}>
          <View style={[styles.full1]} />
          <Text style={[commonStyles.titles, styles.textHeader, styles.full2]}>
            Statistiken der aktiven Übungssets anzeigen
          </Text>
          {/* Empty View (not to position the arrow absolute) */}
          <View style={styles.full1} />
        </View>
        <View
          style={[
            styles.allowTherapistSwitchContainer,
            responsiveStyles.responsiveSwitcherWrapperStyle,
          ]}
        >
          <View
            style={[
              styles.allowTherapistSwitchWrapper,
              responsiveStyles.wrapperPadding,
            ]}
          >
            <Text style={responsiveStyles.switchLabelStyle}>
              Ergebnisse mit dem verbundenen Therapeuten-Account teilen
            </Text>
            <ToggleTherapistCanSeeResults showErrorMessage={showErrorMessage} />
          </View>
        </View>
        <View style={styles.centerPosition}>
          <View style={responsiveStyles.setBoxesWrapper}>
            <SquareGrid items={exerciseSets} rows={2} columns={2} />
          </View>
        </View>
      </View>
      <View style={responsiveStyles.footerContainer}>
        <Text>
          <View>
            <Text style={[responsiveStyles.footerTextStyle]}>
              Download der{' '}
            </Text>
          </View>
          <Pressable onPress={onDownloadModalShow}>
            <Text style={[styles.linkStyle, responsiveStyles.footerTextStyle]}>
              Übungsergebnisse und Übungsdauer
            </Text>
          </Pressable>{' '}
          <View>
            <Text style={[responsiveStyles.footerTextStyle]}>
              als interoperables Bundle des MIO DiGA Toolkit
            </Text>
          </View>
        </Text>
      </View>
    </ScrollView>
  );
}

export default Results;
